import { supabase } from "@/integrations/supabase/client";

export interface CampaignProgress {
  totalTargets: number;
  completed: number;
  failed: number;
  replied: number;
  inProgress: number;
  pending: number;
  progressPercentage: number;
  estimatedDaysRemaining: number;
  estimatedCompletionDate: Date;
  completedJobs: number;
  pendingJobs: number;
  adjustedExpectedJobs: number;
  totalSequenceSteps: number;
}

export async function calculateCampaignProgress(
  campaignId: string
): Promise<CampaignProgress> {
  // All three lookups run in parallel; counts are done in the database so
  // campaigns with more than 1,000 people are counted correctly.
  const [{ data: campaign }, { data: sequences }, { data: counts, error }] = await Promise.all([
    supabase.from('campaigns').select('max_connections_daily, status').eq('id', campaignId).single(),
    supabase.from('sequences').select('id, action_type').eq('campaign_id', campaignId),
    (supabase as any).rpc('campaign_progress_counts', { p_campaign_id: campaignId }),
  ]);
  if (error) throw error;

  const totalSequenceSteps = sequences?.filter(s => s.action_type !== 'end').length || 1;
  const t: Record<string, number> = (counts as any)?.targets || {};
  const j: Record<string, number> = (counts as any)?.jobs || {};
  const sum = (m: Record<string, number>, keys: string[]) => keys.reduce((a, k) => a + (Number(m[k]) || 0), 0);

  const totalTargets = Object.values(t).reduce((a, n) => a + (Number(n) || 0), 0);
  const completed = sum(t, ['completed', 'campaign_completed', 'withdrawn']);
  const failed = sum(t, ['failed']);
  const replied = sum(t, ['replied']);
  const pending = sum(t, ['pending']);
  const inProgress = sum(t, ['connection_pending', 'messaged', 'connected', 'visited', 'in_progress']);
  const completedJobs = sum(j, ['completed', 'cancelled']);
  const pendingJobs = sum(j, ['pending', 'scheduled', 'processing']);
  
  // For backward compatibility, keep adjustedExpectedJobs
  const adjustedExpectedJobs = totalTargets * totalSequenceSteps || 1;
  
  // SIMPLE PROGRESS CALCULATION:
  // Progress = (targets with sequence completed + failed + replied) / total targets
  // Example: 10 targets, 2 failed, 3 completed → (2+3)/10 = 50%
  const finishedTargets = completed + failed + replied;
  
  // If campaign status is 'completed', always show 100%
  const progressPercentage = campaign?.status === 'completed'
    ? 100
    : totalTargets > 0 
      ? Math.round((finishedTargets / totalTargets) * 100)
      : 0;
  
  // Estimate remaining days based on pending jobs
  const dailyConnectionLimit = campaign?.max_connections_daily || 20;
  const estimatedDaysRemaining = pendingJobs > 0 
    ? Math.ceil(pendingJobs / dailyConnectionLimit)
    : 0;
  
  const estimatedCompletionDate = new Date();
  estimatedCompletionDate.setDate(estimatedCompletionDate.getDate() + estimatedDaysRemaining);
  
  return {
    totalTargets,
    completed,
    failed,
    replied,
    inProgress,
    pending,
    progressPercentage,
    estimatedDaysRemaining,
    estimatedCompletionDate,
    completedJobs,
    pendingJobs,
    adjustedExpectedJobs,
    totalSequenceSteps
  };
}
